import { inject } from '@angular/core';
import { signalStore, withState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Events, withEventHandlers, withReducer } from '@ngrx/signals/events';
import { immerOn } from 'ngrx-immer/signals';
import { mapResponse } from '@ngrx/operators';
import { exhaustMap, switchMap } from 'rxjs';
import {
  GroupDetail,
  groupsEvents,
  PREDICTION_GAME_SERVICE,
  RankingFilterOption,
  UserGroup,
} from '@kikkz/shared/prediction-game/util';
import { SNACKBAR_SERVICE } from '@kikkz/shared/snackbar/util';
import { TRANSLATION_SERVICE } from '@kikkz/shared/i18n/util';
import { LOGGING_SERVICE } from '@kikkz/shared/logging/util';
import { serverErrorMessage } from './server-error-message';
import { asyncReducers } from '@kikkz/shared/state/util';
import { rankingFilterSequence } from './ranking-filter-sequence';

export interface GroupsState {
  userGroups: UserGroup[];
  isLoadingUserGroups: boolean;
  isJoiningGroup: boolean;
  isCreatingGroup: boolean;
  isLeavingGroup: boolean;
  /** The group whose detail view is open — the anchor for every follow-up request. */
  selectedGroupId: string | null;
  /** Server-defined filter options of the opened group's ranking. */
  groupRankingFilterOptions: RankingFilterOption[];
  isLoadingGroupRankingFilterOptions: boolean;
  /** The option whose group ranking is currently shown — owned by the store. */
  selectedGroupFilterOption: RankingFilterOption | null;
  selectedGroup: GroupDetail | null;
  isLoadingGroupDetail: boolean;
  userGroupsError: string | null;
  /**
   * The opened group's error — written by BOTH detail requests: without the server's
   * filter options there is no group ranking the backend would answer, so a failure of
   * either one is the detail view's error.
   */
  groupDetailError: string | null;
}

const initialState: GroupsState = {
  userGroups: [],
  isLoadingUserGroups: false,
  isJoiningGroup: false,
  isCreatingGroup: false,
  isLeavingGroup: false,
  selectedGroupId: null,
  groupRankingFilterOptions: [],
  isLoadingGroupRankingFilterOptions: false,
  selectedGroupFilterOption: null,
  selectedGroup: null,
  isLoadingGroupDetail: false,
  userGroupsError: null,
  groupDetailError: null,
};

/** Everything that belongs to the opened group — cleared together, never piecemeal. */
function clearGroupDetail(state: GroupsState): void {
  state.selectedGroup = null;
  state.selectedGroupId = null;
  state.groupRankingFilterOptions = [];
  state.selectedGroupFilterOption = null;
  state.groupDetailError = null;
  // Both the filter-options and the detail region guard their success/failed reducers with
  // `accepts`/`belongsToSelectedGroup`, so a late answer for the group we're leaving is
  // already dropped as a no-op once `selectedGroupId` is nulled above. But `accepts` only
  // guards the answer reducer, never `onStart` — and the request for the NEXT group is a
  // separate dispatch that sets its own loading flag independently. So the OLD, now-
  // abandoned request's flag is never touched again by anything and has to be reset here,
  // or it would stay stuck `true` forever.
  state.isLoadingGroupDetail = false;
  state.isLoadingGroupRankingFilterOptions = false;
}

/** Whether an answer for `groupId` still belongs to the currently opened group. */
function belongsToSelectedGroup(state: GroupsState, groupId: string): boolean {
  return state.selectedGroupId === groupId;
}

/**
 * Prediction groups region: the user's groups, join/create/leave and the opened group's
 * ranking with its own server-driven filter options.
 *
 * Like the global ranking, the sequence "load the group's options → preselect the first
 * one → load that ranking" is a store effect rather than tab logic.
 */
export const GroupsStore = signalStore(
  withState(initialState),
  withDevtools('prediction-game-groups'),
  withReducer(
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.loadUserGroups,
      success: groupsEvents.loadUserGroupsSuccess,
      failed: groupsEvents.loadUserGroupsFailed,
      loading: 'isLoadingUserGroups',
      error: 'userGroupsError',
      onSuccess: (state, payload) => {
        state.userGroups = payload.groups;
      },
    }),
    // Join and create surface their failure as a snackbar (see the effects below), so
    // neither carries an error field — hence no `error` key here.
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.joinGroup,
      success: groupsEvents.joinGroupSuccess,
      failed: groupsEvents.joinGroupFailed,
      loading: 'isJoiningGroup',
      onSuccess: (state, payload) => {
        state.userGroups.push(payload.group);
      },
    }),
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.createGroup,
      success: groupsEvents.createGroupSuccess,
      failed: groupsEvents.createGroupFailed,
      loading: 'isCreatingGroup',
      onSuccess: (state, payload) => {
        state.userGroups.push(payload.group);
      },
    }),
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.leaveGroup,
      success: groupsEvents.leaveGroupSuccess,
      failed: groupsEvents.leaveGroupFailed,
      loading: 'isLeavingGroup',
      onSuccess: (state, payload) => {
        state.userGroups = state.userGroups.filter((group) => group.id !== payload.groupId);
        // Leaving is only reachable from that group's detail view, so its whole detail
        // state goes with it — including the filter options, which would otherwise leak
        // into the next group the user opens.
        if (state.selectedGroupId === payload.groupId || state.selectedGroup?.id === payload.groupId) {
          clearGroupDetail(state);
        }
      },
    }),
    // Failing to load the options means there is no group ranking the backend would answer
    // (it matches the option verbatim), so this region writes the DETAIL view's error:
    // cleared when the group is opened, set when the options fail — `kikkz-async-content`
    // then renders the error card with its retry.
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.loadGroupRankingFilterOptions,
      success: groupsEvents.loadGroupRankingFilterOptionsSuccess,
      failed: groupsEvents.loadGroupRankingFilterOptionsFailed,
      loading: 'isLoadingGroupRankingFilterOptions',
      error: 'groupDetailError',
      // Ignore an answer for a group the user has meanwhile navigated away from. The
      // guard cannot apply to the load itself: that is where the anchor moves.
      accepts: (state, payload) => belongsToSelectedGroup(state, payload.groupId),
      onStart: (state, payload) => {
        state.selectedGroupId = payload.groupId;
        // Reset so options of a previously opened group never leak into the next
        // group's initial selection while the new group's options are loading.
        state.groupRankingFilterOptions = [];
        state.selectedGroupFilterOption = null;
      },
      onSuccess: (state, payload) => {
        state.groupRankingFilterOptions = payload.options;
      },
    }),
    immerOn(groupsEvents.selectGroupFilterOption, (state, { payload }) => {
      state.selectedGroupFilterOption = payload.option;
    }),
    ...asyncReducers<GroupsState>()({
      load: groupsEvents.loadGroupDetail,
      success: groupsEvents.loadGroupDetailSuccess,
      failed: groupsEvents.loadGroupDetailFailed,
      loading: 'isLoadingGroupDetail',
      error: 'groupDetailError',
      // Same reasoning as the filter-options region above: ignore an answer for a group
      // the user has meanwhile navigated away from.
      accepts: (state, payload) => belongsToSelectedGroup(state, payload.groupId),
      onStart: (state, payload) => {
        state.selectedGroupId = payload.groupId;
      },
      onSuccess: (state, payload) => {
        state.selectedGroup = payload.detail;
      },
    }),
    immerOn(groupsEvents.deselectGroup, (state) => {
      clearGroupDetail(state);
    }),
  ),
  withEventHandlers(
    (
      store,
      events = inject(Events),
      service = inject(PREDICTION_GAME_SERVICE),
      snackbar = inject(SNACKBAR_SERVICE),
      translation = inject(TRANSLATION_SERVICE),
      logger = inject(LOGGING_SERVICE),
    ) => {
      // "options arrived → preselect the first one → load that ranking", shared with the
      // global ranking. The two guards are what makes it group-aware: an answer for a group
      // the user has navigated away from must not overwrite the current selection, and
      // without an opened group there is no detail to load.
      const filterSequence = rankingFilterSequence({
        events,
        optionsLoaded: groupsEvents.loadGroupRankingFilterOptionsSuccess,
        selectOption: groupsEvents.selectGroupFilterOption,
        // Same check as `belongsToSelectedGroup` above, applied to the store's live signal
        // instead of a state draft — kept inline rather than forcing both receiver shapes
        // through one signature.
        isStillCurrent: (payload) => payload.groupId === store.selectedGroupId(),
        loadRanking: (option) => {
          const groupId = store.selectedGroupId();
          return groupId === null ? null : groupsEvents.loadGroupDetail({ groupId, filterOption: option });
        },
      });

      return {
        loadUserGroups$: events.on(groupsEvents.loadUserGroups).pipe(
          switchMap(() =>
            service.loadUserGroups().pipe(
              mapResponse({
                next: (groups) => groupsEvents.loadUserGroupsSuccess({ groups }),
                error: (error: unknown) =>
                  groupsEvents.loadUserGroupsFailed({
                    error: serverErrorMessage(error, 'pg.errors.userGroupsLoad', translation, logger),
                  }),
              }),
            ),
          ),
        ),
        joinGroup$: events.on(groupsEvents.joinGroup).pipe(
          exhaustMap(({ payload }) =>
            service.joinGroup(payload.code).pipe(
              mapResponse({
                next: (group) => groupsEvents.joinGroupSuccess({ group }),
                error: (error: unknown) => {
                  const message = serverErrorMessage(error, 'pg.errors.groupJoin', translation, logger);
                  snackbar.error(message);
                  return groupsEvents.joinGroupFailed({ error: message });
                },
              }),
            ),
          ),
        ),
        createGroup$: events.on(groupsEvents.createGroup).pipe(
          exhaustMap(({ payload }) =>
            service.createGroup(payload.name).pipe(
              mapResponse({
                next: (group) => groupsEvents.createGroupSuccess({ group }),
                error: (error: unknown) => {
                  const message = serverErrorMessage(error, 'pg.errors.groupCreate', translation, logger);
                  snackbar.error(message);
                  return groupsEvents.createGroupFailed({ error: message });
                },
              }),
            ),
          ),
        ),
        leaveGroup$: events.on(groupsEvents.leaveGroup).pipe(
          exhaustMap(({ payload }) =>
            service.leaveGroup(payload.groupId).pipe(
              mapResponse({
                next: () => groupsEvents.leaveGroupSuccess({ groupId: payload.groupId }),
                error: (error: unknown) => {
                  const message = serverErrorMessage(error, 'pg.errors.groupLeave', translation, logger);
                  snackbar.error(message);
                  return groupsEvents.leaveGroupFailed({ error: message });
                },
              }),
            ),
          ),
        ),
        loadGroupRankingFilterOptions$: events.on(groupsEvents.loadGroupRankingFilterOptions).pipe(
          switchMap(({ payload }) =>
            service.loadGroupRankingFilterOptions(payload.groupId).pipe(
              mapResponse({
                next: (options) =>
                  groupsEvents.loadGroupRankingFilterOptionsSuccess({ groupId: payload.groupId, options }),
                error: (error: unknown) =>
                  groupsEvents.loadGroupRankingFilterOptionsFailed({
                    groupId: payload.groupId,
                    error: serverErrorMessage(error, 'pg.errors.groupRankingFilterOptionsLoad', translation, logger),
                  }),
              }),
            ),
          ),
        ),
        selectInitialGroupFilterOption$: filterSequence.selectInitialOption$,
        loadGroupDetailOnSelect$: filterSequence.loadOnSelect$,
        loadGroupDetail$: events.on(groupsEvents.loadGroupDetail).pipe(
          switchMap(({ payload }) =>
            service.loadGroupDetail(payload.groupId, payload.filterOption).pipe(
              mapResponse({
                next: (detail) => groupsEvents.loadGroupDetailSuccess({ groupId: payload.groupId, detail }),
                error: (error: unknown) =>
                  groupsEvents.loadGroupDetailFailed({
                    groupId: payload.groupId,
                    error: serverErrorMessage(error, 'pg.errors.groupDetailLoad', translation, logger),
                  }),
              }),
            ),
          ),
        ),
      };
    },
  ),
);
